<template>
  <div class="clock-interface">
    <div class="clock-container">
      <!-- Status Card -->
      <div class="status-card">
        <div class="current-time">
          <h2>{{ currentTime }}</h2>
          <p>{{ currentDate }}</p>
        </div>

        <div class="current-status">
          <div
            :class="[
              'status-badge',
              todayRecord?.clock_out_time
                ? 'completed'
                : todayRecord?.clock_in_time &&
                  !todayRecord?.clock_out_time &&
                  todayRecord?.field_approval_status !== 'pending'
                ? 'active'
                : todayRecord?.field_approval_status === 'pending'
                ? 'pending'
                : 'not-started',
            ]"
          >
            <Clock class="w-4 h-4" />
            <span>
              {{
                todayRecord?.clock_out_time
                  ? 'Work Completed'
                  : todayRecord?.clock_in_time &&
                    !todayRecord?.clock_out_time &&
                    todayRecord?.field_approval_status !== 'pending'
                  ? 'Currently Working'
                  : todayRecord?.field_approval_status === 'pending'
                  ? 'Pending Approval'
                  : 'Not Started'
              }}
            </span>
          </div>

          <div
            v-if="todayRecord?.clock_in_time && !todayRecord?.clock_out_time"
            class="work-duration"
          >
            <Timer class="w-4 h-4" />
            {{ workDuration }}
          </div>
        </div>
      </div>

      <!-- Message Display -->
      <div v-if="message" :class="['message', messageType]">
        <div class="message-content">
          <CheckCircle v-if="messageType === 'success'" class="w-5 h-5" />
          <AlertCircle v-else class="w-5 h-5" />
          <span>{{ message }}</span>
        </div>
        <button @click="clearMessage" class="message-close">
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Device Security Warning - Removed: Security handled at login level -->
      <!-- Device approval is now enforced during authentication, not at clock-in/out -->

      <!-- Completed Day View -->
      <div v-if="todayRecord?.clock_out_time && !forceShowClockIn" class="completed-day">
        <div class="completion-icon">
          <CheckCircle class="w-16 h-16" />
        </div>
        <h3>Work Day Completed!</h3>
        <p>You've successfully completed your work day.</p>

        <div class="day-summary">
          <div class="summary-item">
            <label>Clock In Time:</label>
            <span>{{ formatTime(todayRecord.clock_in_time) }}</span>
          </div>
          <div class="summary-item">
            <label>Clock Out Time:</label>
            <span>{{ formatTime(todayRecord.clock_out_time) }}</span>
          </div>
          <div class="summary-item">
            <label>Total Duration:</label>
            <span>{{ formatDuration(todayRecord.work_duration) }}</span>
          </div>
          <div class="summary-item">
            <label>Work Mode:</label>
            <span>{{
              todayRecord.attendance_mode === 'field' ? 'Field Work' : 'Office'
            }}</span>
          </div>
        </div>

        <!-- Start New Session Button -->
        <div class="new-session-section">
          <p class="new-session-info">Need to work again today? Start a new session!</p>
          <button 
            @click="startNewSession" 
            class="btn-new-session"
            :disabled="processing"
          >
            <Plus class="w-5 h-5" />
            {{ processing ? 'Loading...' : 'Start New Session' }}
          </button>
        </div>
      </div>

      <!-- Active Session Info -->
      <div
        v-else-if="todayRecord?.clock_in_time && !todayRecord?.clock_out_time && !forceShowClockIn"
        class="session-info"
      >
        <div class="session-detail">
          <Clock class="w-5 h-5" />
          <span>Clocked in at {{ formatTime(todayRecord.clock_in_time) }}</span>
        </div>
        <div class="session-detail">
          <MapPin class="w-5 h-5" />
          <span>{{ todayRecord.clock_in_address || 'Location recorded' }}</span>
        </div>
        <div class="session-detail">
          <Briefcase class="w-5 h-5" />
          <span>{{
            todayRecord.attendance_mode === 'field'
              ? 'Field Work'
              : 'Office Work'
          }}</span>
        </div>
        <div
          v-if="todayRecord.field_approval_status === 'pending'"
          class="session-detail pending"
        >
          <AlertCircle class="w-5 h-5" />
          <span>Waiting for admin approval (Field Work)</span>
        </div>

        <!-- Break Status Section -->
        <div v-if="breakStatus?.isOnBreak" class="break-status-section">
          <div class="break-status-card">
            <div class="break-status-header">
              <div class="break-icon">
                <Coffee class="w-6 h-6" />
              </div>
              <div>
                <h4>On Break</h4>
                <p>{{ breakStatus.break.type }}</p>
              </div>
            </div>
            <div class="break-time-remaining">
              <Clock class="w-4 h-4" />
              <span>{{ breakStatus.timeRemaining?.formatted || '0m' }} remaining</span>
            </div>
            <div class="break-end-time">
              <span>Break ends at {{ formatTime(breakStatus.break.endTime) }}</span>
            </div>
          </div>
        </div>

        <!-- Upcoming Break Warning -->
        <div v-if="upcomingBreak" class="upcoming-break-warning">
          <div class="warning-icon">
            <AlertTriangle class="w-5 h-5" />
          </div>
          <div>
            <h4>Break Starting Soon</h4>
            <p>{{ upcomingBreak.message }}</p>
          </div>
        </div>

        <!-- Auto Clock-Out Notice -->
        <div v-if="shouldAutoClockOut" class="auto-action-notice">
          <div class="notice-icon">
            <LogOut class="w-5 h-5" />
          </div>
          <div>
            <h4>Automatic Clock-Out</h4>
            <p>{{ autoActionMessage }}</p>
            <button @click="handleAutoClockOut" class="btn-auto-action" :disabled="processing">
              <Loader v-if="processing" class="w-4 h-4 animate-spin" />
              <LogOut v-else class="w-4 h-4" />
              {{ processing ? 'Processing...' : 'Clock Out for Break' }}
            </button>
          </div>
        </div>

        <!-- Short Leave Clock Section (TASK-031) — now requires an approved,
             scheduled request; Leave In only appears once its window is open. -->
        <div v-if="!isOnShortLeave && canLeaveIn" class="short-leave-section">
          <h4>Approved Short Leave - Ready</h4>
          <p>Take a photo to start your short leave</p>
          <button @click="startLeaveIn" class="btn-leave">
            <LogOut class="w-4 h-4" />
            Leave In
          </button>
        </div>

        <!-- Upcoming approved short leave — not open yet -->
        <div v-else-if="!isOnShortLeave && shortLeaveSchedule.upcoming" class="short-leave-section short-leave-upcoming">
          <h4>Short Leave Scheduled</h4>
          <p>
            Approved for {{ formatTime(shortLeaveSchedule.upcoming.start_date) }}
            to {{ formatTime(shortLeaveSchedule.upcoming.end_date) }}.
            You'll be reminded 5 minutes before it starts.
          </p>
        </div>

        <!-- No schedule at all -->
        <div v-else-if="!isOnShortLeave" class="short-leave-section short-leave-none">
          <h4>Need to step out?</h4>
          <p>Request a short leave in advance. Leave In unlocks once it's approved and the time window opens.</p>
        </div>

        <!-- Active Short Leave Status -->
        <div v-if="isOnShortLeave" class="active-leave-banner">
          <div class="active-leave-header">
            <div class="active-leave-icon">
              <Clock class="w-6 h-6" />
            </div>
            <div>
              <h4>Currently on Short Leave</h4>
              <p>Out since {{ formatTime(activeShortLeaveSession?.leaveInTime) }}, {{ leaveElapsedMinutes }} min elapsed</p>
            </div>
          </div>
          <button @click="startLeaveOut" class="btn-leave-out">
            <LogIn class="w-4 h-4" />
            Leave Out (Return to Work)
          </button>
        </div>

        <!-- Clock Out Section for Active Session -->
        <div class="clock-out-section" style="margin-top: 25px; text-align: center;">
          <h4>End Work Session</h4>
          <p>Take a photo to clock out of your current session</p>
          
          <!-- Camera Section for Clock Out -->
          <div class="camera-section" style="margin-bottom: 20px;">
            <!-- Camera Placeholder -->
            <div
              v-if="!cameraActive && !capturedPhoto"
              class="camera-placeholder"
            >
              <div class="camera-icon-container">
                <Camera class="w-12 h-12" />
                <div class="camera-pulse"></div>
              </div>
              <p>Take a photo to verify clock-out</p>
              <button @click="startCamera" class="btn-camera">
                <Camera class="w-4 h-4" />
                Start Camera
              </button>
            </div>

            <!-- Active Camera View -->
            <div v-if="cameraActive" class="camera-container">
              <div class="camera-wrapper">
                <video
                  ref="videoElement"
                  autoplay
                  playsinline
                  class="camera-preview"
                ></video>

                <!-- Camera Status Overlay -->
                <div class="camera-status-overlay">
                  <div v-if="cameraReady" class="status-indicator ready">
                    <CheckCircle class="w-4 h-4" />
                    Camera Ready
                  </div>
                  <div v-else class="status-indicator loading">
                    <Loader class="w-4 h-4 animate-spin" />
                    Preparing Camera...
                  </div>
                </div>

                <!-- Face Guide Overlay -->
                <div class="face-guide-overlay">
                  <div class="face-outline"></div>
                  <p class="face-guide-text">
                    Position your face within the oval
                  </p>
                </div>
              </div>

              <div class="camera-controls">
                <button
                  @click="capturePhoto"
                  :disabled="!cameraReady"
                  class="btn-capture"
                >
                  <Camera class="w-5 h-5" />
                  Take Photo
                </button>
                <button @click="stopCamera" class="btn-secondary">
                  <X class="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>

            <!-- Photo Preview -->
            <div v-if="capturedPhoto" class="photo-preview">
              <img
                :src="capturedPhoto"
                alt="Captured photo"
                class="preview-image"
              />
              <div class="photo-success">
                <CheckCircle class="w-5 h-5" />
                Photo captured successfully
              </div>
              <div class="photo-controls">
                <button @click="retakePhoto" class="btn-retake">
                  <RotateCcw class="w-4 h-4" />
                  Retake Photo
                </button>
              </div>
            </div>
          </div>

          <!-- Clock Out Button - Always show when user is clocked in, regardless of canClockOut computed -->
          <button
            v-if="todayRecord?.clock_in_time && !todayRecord?.clock_out_time"
            @click="clockOut"
            :disabled="!canClockOutNow || processing"
            :class="['clock-btn', 'clock-out-btn', { processing }]"
            style="margin-top: 15px;"
          >
            <Loader v-if="processing" class="w-5 h-5 animate-spin" />
            <LogOut v-else class="w-5 h-5" />
            {{ processing ? 'Clocking Out...' : 'Clock Out' }}
          </button>
          <div v-if="isOnShortLeave" class="short-leave-block-hint">
            You are currently on short leave. Please <strong>Leave Out</strong> before clocking out.
          </div>


          <!-- Requirements for Clock Out -->
          <div class="requirements-checklist" style="margin-top: 20px;">
            <h4>Requirements</h4>
            <div :class="['requirement-item', { met: !!capturedPhoto }]">
              <Camera class="w-4 h-4" />
              <span>Photo captured</span>
            </div>
            <div :class="['requirement-item', { met: !!locationData }]">
              <MapPin class="w-4 h-4" />
              <span>Location access granted</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Clock Action Section -->
      <div v-else class="clock-action-section">
        <h3>{{ getWorkDayAction() }}</h3>
        <p>
          {{ getWorkDayInstructions() }}
        </p>

        <!-- Location will be auto-detected, no manual selection needed -->

        <!-- Enhanced Camera Section with Photo Review -->
        <div class="camera-section">
          <h4>Photo Verification Required</h4>
          <p class="camera-description">
            Take a clear photo of yourself for attendance verification
          </p>

          <!-- Camera Placeholder -->
          <div
            v-if="!cameraActive && !capturedPhoto"
            class="camera-placeholder"
          >
            <div class="camera-icon-container">
              <Camera class="w-12 h-12" />
              <div class="camera-pulse"></div>
            </div>
            <p>Take a photo to verify your identity</p>
            <div class="camera-guidelines">
              <div class="guideline-item">
                <CheckCircle class="w-4 h-4" />
                <span>Look directly at the camera</span>
              </div>
              <div class="guideline-item">
                <CheckCircle class="w-4 h-4" />
                <span>Ensure good lighting</span>
              </div>
              <div class="guideline-item">
                <CheckCircle class="w-4 h-4" />
                <span>Keep face clearly visible</span>
              </div>
            </div>
            <button @click="startCamera" class="btn-camera">
              <Camera class="w-4 h-4" />
              Start Camera
            </button>
          </div>

          <!-- Active Camera View -->
          <div v-if="cameraActive" class="camera-container">
            <div class="camera-wrapper">
              <video
                ref="videoElement"
                autoplay
                playsinline
                class="camera-preview"
              ></video>

              <!-- Camera Status Overlay -->
              <div class="camera-status-overlay">
                <div v-if="cameraReady" class="status-indicator ready">
                  <CheckCircle class="w-4 h-4" />
                  Camera Ready
                </div>
                <div v-else class="status-indicator loading">
                  <Loader class="w-4 h-4 animate-spin" />
                  Preparing Camera...
                </div>
              </div>

              <!-- Face Guide Overlay -->
              <div class="face-guide-overlay">
                <div class="face-outline"></div>
                <p class="face-guide-text">
                  Position your face within the oval
                </p>
              </div>
            </div>

            <div class="camera-controls">
              <button
                @click="capturePhoto"
                :disabled="!cameraReady"
                class="btn-capture"
              >
                <Camera class="w-5 h-5" />
                Take Photo
              </button>
              <button @click="stopCamera" class="btn-secondary">
                <X class="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>

          <!-- Photo Preview -->
          <div v-if="capturedPhoto" class="photo-preview">
            <img
              :src="capturedPhoto"
              alt="Captured photo"
              class="preview-image"
            />
            <div class="photo-success">
              <CheckCircle class="w-5 h-5" />
              Photo captured successfully
            </div>
            <div class="photo-controls">
              <button @click="retakePhoto" class="btn-retake">
                <RotateCcw class="w-4 h-4" />
                Retake Photo
              </button>
            </div>
          </div>
        </div>

        <!-- Requirements Checklist -->
        <div class="requirements-checklist">
          <h4>Requirements</h4>
          <div :class="['requirement-item', { met: !!capturedPhoto }]">
            <Camera class="w-4 h-4" />
            <span>Photo captured</span>
          </div>
          <div :class="['requirement-item', { met: !!locationData }]">
            <MapPin class="w-4 h-4" />
            <span>Location access granted</span>
          </div>
        </div>

        <!-- Clock In/Out Buttons -->
        <div class="clock-buttons">
          
          <button
            v-if="canClockIn"
            @click="clockIn"
            :disabled="!canClockInNow || processing"
            :class="['clock-btn', 'clock-in-btn', { processing }]"
          >
            <Loader v-if="processing" class="w-5 h-5 animate-spin" />
            <LogIn v-else class="w-5 h-5" />
            {{ processing ? 'Clocking In...' : 'Clock In' }}
          </button>

          <!-- Fallback button for debugging -->
          <button
            v-if="!canClockIn && capturedPhoto && locationData"
            @click="clockIn"
            style="background: red; color: white; padding: 10px 20px; border: none; border-radius: 5px; margin: 10px 0;"
          >
            🔴 DEBUG CLOCK IN (Force)
          </button>

          <button
            v-if="canClockOut"
            @click="clockOut"
            :disabled="!canClockOutNow || processing"
            :class="['clock-btn', 'clock-out-btn', { processing }]"
          >
            <Loader v-if="processing" class="w-5 h-5 animate-spin" />
            <LogOut v-else class="w-5 h-5" />
            {{ processing ? 'Clocking Out...' : 'Clock Out' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Leave In / Leave Out Camera Modal (TASK-031) -->
    <div
      v-if="showLeaveClockModal"
      class="modal-overlay"
      @click.self="cancelLeaveClockModal"
    >
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ leaveClockMode === 'in' ? 'Leave In - Take Photo' : 'Leave Out - Take Photo' }}</h3>
          <button @click="cancelLeaveClockModal"><X class="w-5 h-5" /></button>
        </div>

        <div style="padding: 0 25px 25px 25px;">
          <p style="color:#666;margin-bottom:16px;">
            {{ leaveClockMode === 'in'
              ? 'Take a photo to record your Leave In time and location.'
              : 'Take a photo to record your return to work.' }}
          </p>

          <!-- Camera Section -->
          <div class="camera-section">
            <div v-if="!leaveCameraActive && !leaveCapturedPhoto" class="camera-placeholder">
              <div class="camera-icon-container">
                <Camera class="w-12 h-12" />
                <div class="camera-pulse"></div>
              </div>
              <p>Take a photo to verify</p>
              <button @click="startLeaveCamera" class="btn-camera">
                <Camera class="w-4 h-4" /> Start Camera
              </button>
            </div>

            <div v-if="leaveCameraActive" class="camera-container">
              <div class="camera-wrapper">
                <video ref="leaveVideoElement" autoplay playsinline class="camera-preview"></video>
                <div class="camera-status-overlay">
                  <div v-if="leaveCameraReady" class="status-indicator ready">
                    <CheckCircle class="w-4 h-4" /> Camera Ready
                  </div>
                  <div v-else class="status-indicator loading">
                    <Loader class="w-4 h-4 animate-spin" /> Preparing...
                  </div>
                </div>
                <div class="face-guide-overlay">
                  <div class="face-outline"></div>
                  <p class="face-guide-text">Position your face within the oval</p>
                </div>
              </div>
              <div class="camera-controls">
                <button @click="captureLeavePhoto" :disabled="!leaveCameraReady" class="btn-capture">
                  <Camera class="w-5 h-5" /> Take Photo
                </button>
                <button @click="stopLeaveCamera" class="btn-secondary">
                  <X class="w-4 h-4" /> Cancel
                </button>
              </div>
            </div>

            <div v-if="leaveCapturedPhoto" class="photo-preview">
              <img :src="leaveCapturedPhoto" alt="Leave photo" class="preview-image" />
              <div class="photo-success"><CheckCircle class="w-5 h-5" /> Photo captured</div>
              <div class="photo-controls">
                <button @click="retakeLeavePhoto" class="btn-retake">
                  <RotateCcw class="w-4 h-4" /> Retake
                </button>
              </div>
            </div>
          </div>

          <!-- Requirements -->
          <div class="requirements-checklist" style="margin-top:16px;">
            <div :class="['requirement-item', { met: !!leaveCapturedPhoto }]">
              <Camera class="w-4 h-4" /><span>Photo captured</span>
            </div>
            <div :class="['requirement-item', { met: !!locationData }]">
              <MapPin class="w-4 h-4" /><span>Location access granted</span>
            </div>
          </div>

          <div class="form-actions" style="margin-top:20px;">
            <button @click="cancelLeaveClockModal" class="btn-secondary">Cancel</button>
            <button
              @click="submitLeaveClock"
              :disabled="!leaveCapturedPhoto || !locationData || processing"
              :class="['btn-primary', leaveClockMode === 'in' ? 'leave-in-btn' : 'leave-out-btn']"
            >
              <Loader v-if="processing" class="w-4 h-4 animate-spin" />
              <LogOut v-else-if="leaveClockMode === 'in'" class="w-4 h-4" />
              <LogIn  v-else class="w-4 h-4" />
              {{ processing ? 'Submitting...' : (leaveClockMode === 'in' ? 'Confirm Leave In' : 'Confirm Leave Out') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, reactive, nextTick } from 'vue';
import { api } from '@/services/api';
import {
  Clock,
  Timer,
  CheckCircle,
  AlertCircle,
  X,
  Shield,
  MapPin,
  Camera,
  Building,
  Briefcase,
  Plus,
  RotateCcw,
  LogIn,
  LogOut,
  Loader,
  Eye,
  FileImage,
  Sparkles,
  Coffee,
  AlertTriangle,
} from 'lucide-vue-next';

// Reactive state
const currentTime = ref('');
const currentDate = ref('');
const todayRecord = ref(null);
const workDuration = ref('0h 0m');
const processing = ref(false);
const message = ref('');
const messageType = ref('success');

// Camera and photo
const cameraActive = ref(false);
const cameraReady = ref(false);
const capturedPhoto = ref(null);
const videoElement = ref(null);
const mediaStream = ref(null);

// Photo review and validation
const photoTimestamp = ref('');
const photoSize = ref('');
const photoQuality = ref('');
const photoQualityText = ref('');
const faceVisible = ref(false);
const goodLighting = ref(false);
const noBlur = ref(false);
const properAngle = ref(false);
const showEnhancementOptions = ref(false);

// Photo enhancement
const enhancementSettings = reactive({
  brightness: 1,
  contrast: 1,
  saturation: 1,
});
const originalPhoto = ref(null);
const enhancedPhoto = ref(null);

// Location
const locationData = ref(null);
const locationAccuracy = ref(null);

// Work mode and field work
const canWorkField = ref(false);
const clockMode = ref('office');
const fieldLocations = ref([]);
const selectedFieldLocationId = ref(null);
const fieldLocationName = ref('');

// TASK-031: Short leave clock state
const isOnShortLeave = ref(false);
const activeShortLeaveSession = ref(null);
const leaveElapsedMinutes = ref(0);
let leaveElapsedInterval = null;

// Scheduled short leave (requires approval first): open window to Leave In/Out,
// or an upcoming approved request later today.
const shortLeaveSchedule = ref({ open: null, upcoming: null });
let shortLeaveScheduleInterval = null;

const canLeaveIn = computed(() => !!shortLeaveSchedule.value.open);

// Leave In/Out modal state
const showLeaveClockModal = ref(false);
const leaveClockMode = ref('in'); // 'in' | 'out'
const leaveCameraActive = ref(false);
const leaveCameraReady = ref(false);
const leaveCapturedPhoto = ref(null);
const leaveVideoElement = ref(null);
const leaveMediaStream = ref(null);

// New session override
const forceShowClockIn = ref(false);

// Break management
const breakStatus = ref(null);
const upcomingBreak = ref(null);
const shouldAutoClockOut = ref(false);
const shouldAutoClockIn = ref(false);
const autoActionMessage = ref('');

// Intervals
let workDurationInterval = null;
let timeInterval = null;
let breakCheckInterval = null;

// Computed properties
const canClockIn = computed(() => {
  // Allow multiple clock-ins per day:
  // 1. No record for today (haven't started work)
  // 2. Have clocked out (work is completed and can start fresh)  
  // 3. Allow additional clock-ins even after clock-out (for multiple work sessions)
  if (!todayRecord.value) return true;
  if (todayRecord.value.clock_out_time) return true; // Allow new clock-in after clock-out
  
  // Cannot clock in if currently clocked in (must clock out first)
  return !todayRecord.value.isClockedIn;
});

const canClockOut = computed(() => {
  return (
    todayRecord.value?.clock_in_time &&
    !todayRecord.value?.clock_out_time &&
    todayRecord.value?.field_approval_status !== 'pending'
  );
});

const canClockInNow = computed(() => {
  const hasPhoto = !!capturedPhoto.value;
  const hasLocation = !!locationData.value;
  return (
    hasPhoto &&
    hasLocation &&
    !processing.value
  );
});

const canClockOutNow = computed(() => {
  if (isOnShortLeave.value) return false;
  const hasPhoto = !!capturedPhoto.value;
  const hasLocation = !!locationData.value;
  return hasPhoto && hasLocation && !processing.value;
});


// Methods
const updateCurrentTime = () => {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  currentDate.value = now.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const loadUserProfile = async () => {
  try {
    const response = await api.get('/auth/me');
    canWorkField.value = response.data.can_work_field;
  } catch (error) {
    console.error('Error loading user profile:', error);
  }
};

const loadTodayRecord = async () => {
  try {
    const response = await api.get('/employee/attendance/today');
    console.log('📊 Attendance data received:', response.data);
    
    // Handle the nested response structure
    if (response.data.success && response.data.data && response.data.data.record) {
      todayRecord.value = response.data.data.record;
      console.log('✅ Today record updated:', todayRecord.value);

      // TASK-031: sync short leave status
      isOnShortLeave.value = !!todayRecord.value.isOnShortLeave;
      activeShortLeaveSession.value = todayRecord.value.activeShortLeaveSession || null;
      if (isOnShortLeave.value && activeShortLeaveSession.value) {
        startLeaveElapsedTimer();
      } else {
        stopLeaveElapsedTimer();
      }

      if (todayRecord.value.isClockedIn && !todayRecord.value.has_pending_requests) {
        console.log('🟢 Employee is currently clocked in - updating interface');
        forceShowClockIn.value = false;
      } else if (!todayRecord.value.isClockedIn && !todayRecord.value.has_pending_requests) {
        console.log('🔴 Employee is currently clocked out - interface ready for next clock-in');
      }
    } else {
      console.warn('⚠️ Unexpected response structure:', response.data);
      todayRecord.value = response.data.record || null;
    }
  } catch (error) {
    console.error('Error loading today record:', error);
    todayRecord.value = null;
  }
};

const loadShortLeaveSchedule = async () => {
  try {
    const response = await api.get('/employee/attendance/short-leave-schedule');
    if (response.data.success) {
      shortLeaveSchedule.value = response.data.data;
    }
  } catch (error) {
    console.error('Error loading short leave schedule:', error);
  }
};

const loadFieldLocations = async () => {
  if (!canWorkField.value) return;

  try {
    const response = await api.get('/field-locations');
    fieldLocations.value = response.data.locations || [];
  } catch (error) {
    console.error('Error loading field locations:', error);
  }
};

// Device approval check removed - security is now handled at login level
// Users who reach this component are already authenticated with approved devices
const checkDeviceApproval = async () => {
  // No longer needed - device approval enforced at authentication
  console.log('🔒 Device approval check skipped - handled at login level');
};

// Device approval request removed - no longer needed
// Device approval is now handled during authentication process
const requestDeviceApproval = async () => {
  showMessage('Device approval is handled during the login process', 'info');
};

const getCurrentLocation = () => {
  if (!navigator.geolocation) {
    showMessage('Geolocation is not supported by this browser', 'error');
    return;
  }

  navigator.geolocation.getCurrentPosition(
    position => {
      locationData.value = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        speed: position.coords.speed,
        heading: position.coords.heading,
      };
      locationAccuracy.value = position.coords.accuracy;
    },
    error => {
      console.error('Location error:', error);
      showMessage('Location access is required for clock in/out', 'error');
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000,
    }
  );
};

const startCamera = async () => {
  try {
    // Reset states
    cameraReady.value = false;

    // Request camera access
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 },
      },
      audio: false,
    });

    // Store the stream
    mediaStream.value = stream;

    // Set camera as active
    cameraActive.value = true;

    // Wait for next tick to ensure DOM is updated
    await nextTick();

    // Connect stream to video element
    if (videoElement.value) {
      videoElement.value.srcObject = stream;

      // Wait for video metadata to load
      videoElement.value.onloadedmetadata = () => {
        // Ensure video starts playing
        videoElement.value
          .play()
          .then(() => {
            // Small delay to ensure stream is stable
            setTimeout(() => {
              cameraReady.value = true;
            }, 1000);
          })
          .catch(error => {
            console.error('Error playing video:', error);
            showMessage('Error starting video preview', 'error');
          });
      };

      // Handle any video errors
      videoElement.value.onerror = error => {
        console.error('Video element error:', error);
        showMessage('Video preview error', 'error');
      };
    } else {
      console.error('Video element not found');
      showMessage('Video element not available', 'error');
    }
  } catch (error) {
    console.error('Camera error:', error);

    let errorMessage = 'Camera access failed';

    switch (error.name) {
      case 'NotAllowedError':
        errorMessage =
          'Camera permission denied. Please allow camera access and try again.';
        break;
      case 'NotFoundError':
        errorMessage = 'No camera found on this device.';
        break;
      case 'NotReadableError':
        errorMessage = 'Camera is already in use by another application.';
        break;
      case 'OverconstrainedError':
        errorMessage = 'Camera does not support the requested settings.';
        break;
      default:
        errorMessage = `Camera error: ${error.message}`;
    }

    showMessage(errorMessage, 'error');

    // Reset states on error
    cameraActive.value = false;
    cameraReady.value = false;
  }
};

// Updated capturePhoto method
const capturePhoto = () => {
  if (!videoElement.value || !cameraReady.value || !mediaStream.value) {
    showMessage('Camera not ready', 'error');
    return;
  }

  try {
    // Create canvas element
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    const video = videoElement.value;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to base64 data URL
    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);

    // Store the captured photo
    capturedPhoto.value = photoDataUrl;

    // Set photo metadata
    photoTimestamp.value = new Date().toLocaleString();

    // Calculate file size estimation
    const sizeInBytes = Math.round((photoDataUrl.length * 3) / 4);
    photoSize.value = formatFileSize(sizeInBytes);

    // Stop the camera after successful capture
    stopCamera();

    showMessage('Photo captured successfully!', 'success');
  } catch (error) {
    console.error('Error capturing photo:', error);
    showMessage('Failed to capture photo', 'error');
  }
};

const retakePhoto = () => {
  capturedPhoto.value = null;
  originalPhoto.value = null;
  enhancedPhoto.value = null;
  showEnhancementOptions.value = false;
  startCamera();
};

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Updated stopCamera method
const stopCamera = () => {
  // Stop all media tracks
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => {
      track.stop();
    });
    mediaStream.value = null;
  }

  // Clear video element
  if (videoElement.value) {
    videoElement.value.srcObject = null;
    videoElement.value.onloadedmetadata = null;
    videoElement.value.onerror = null;
  }

  // Reset states
  cameraActive.value = false;
  cameraReady.value = false;
};

const selectFieldLocation = location => {
  selectedFieldLocationId.value = location.id;
  fieldLocationName.value = location.name;
};

const selectCustomLocation = () => {
  selectedFieldLocationId.value = 'custom';
  fieldLocationName.value = '';
};

const clockIn = async () => {
  if (!canClockInNow.value) return;

  processing.value = true;

  try {
    // Convert photo to blob
    const photoBlob = await fetch(capturedPhoto.value).then(r => r.blob());

    const formData = new FormData();
    formData.append('photo', photoBlob, 'clock-in-photo.jpg');
    formData.append('latitude', locationData.value.latitude);
    formData.append('longitude', locationData.value.longitude);
    formData.append('accuracy', locationData.value.accuracy);
    formData.append('altitude', locationData.value.altitude || '');
    formData.append('speed', locationData.value.speed || '');
    formData.append('heading', locationData.value.heading || '');
    // Mode and deviceFingerprint removed - handled automatically

    const response = await api.post('/employee/attendance/clock-in', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    showMessage(response.data.message || 'Successfully clocked in!', 'success');

    // Reset form
    capturedPhoto.value = null;
    clockMode.value = 'office';
    selectedFieldLocationId.value = null;
    fieldLocationName.value = '';
    forceShowClockIn.value = false; // Reset the override

    // Reload today's record
    await loadTodayRecord();

    // Start work duration timer
    if (
      todayRecord.value?.clock_in_time &&
      !todayRecord.value?.clock_out_time
    ) {
      startWorkDurationTimer();
    }
  } catch (error) {
    console.error('Clock in error:', error);
    showMessage(error.response?.data?.message || 'Failed to clock in', 'error');
  } finally {
    processing.value = false;
  }
};

const clockOut = async () => {
  if (!canClockOutNow.value) return;

  processing.value = true;

  try {
    // Convert photo to blob
    const photoBlob = await fetch(capturedPhoto.value).then(r => r.blob());

    const formData = new FormData();
    formData.append('photo', photoBlob, 'clock-out-photo.jpg');
    formData.append('latitude', locationData.value.latitude);
    formData.append('longitude', locationData.value.longitude);
    formData.append('accuracy', locationData.value.accuracy);
    // Device fingerprint removed - handled at login level

    const response = await api.post('/employee/attendance/clock-out', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    showMessage(
      response.data.message || 'Successfully clocked out!',
      'success'
    );

    // Reset form
    capturedPhoto.value = null;

    // Reload today's record
    await loadTodayRecord();

    // Stop work duration timer
    if (workDurationInterval) {
      clearInterval(workDurationInterval);
      workDurationInterval = null;
    }
  } catch (error) {
    console.error('Clock out error:', error);
    showMessage(
      error.response?.data?.message || 'Failed to clock out',
      'error'
    );
  } finally {
    processing.value = false;
  }
};

// ─── TASK-031: Short Leave Clock In / Out ────────────────────────────────────

const startLeaveIn = () => {
  leaveClockMode.value = 'in';
  leaveCapturedPhoto.value = null;
  showLeaveClockModal.value = true;
};

const startLeaveOut = () => {
  leaveClockMode.value = 'out';
  leaveCapturedPhoto.value = null;
  showLeaveClockModal.value = true;
};

const cancelLeaveClockModal = () => {
  stopLeaveCamera();
  showLeaveClockModal.value = false;
};

const startLeaveCamera = async () => {
  try {
    leaveCameraReady.value = false;
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      audio: false
    });
    leaveMediaStream.value = stream;
    leaveCameraActive.value = true;
    await nextTick();
    if (leaveVideoElement.value) {
      leaveVideoElement.value.srcObject = stream;
      leaveVideoElement.value.onloadedmetadata = () => {
        leaveVideoElement.value.play().then(() => {
          setTimeout(() => { leaveCameraReady.value = true; }, 1000);
        });
      };
    }
  } catch (err) {
    showMessage('Camera access failed: ' + err.message, 'error');
  }
};

const stopLeaveCamera = () => {
  if (leaveMediaStream.value) {
    leaveMediaStream.value.getTracks().forEach(t => t.stop());
    leaveMediaStream.value = null;
  }
  if (leaveVideoElement.value) {
    leaveVideoElement.value.srcObject = null;
  }
  leaveCameraActive.value = false;
  leaveCameraReady.value = false;
};

const captureLeavePhoto = () => {
  if (!leaveVideoElement.value || !leaveCameraReady.value) return;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width  = leaveVideoElement.value.videoWidth;
  canvas.height = leaveVideoElement.value.videoHeight;
  ctx.drawImage(leaveVideoElement.value, 0, 0);
  leaveCapturedPhoto.value = canvas.toDataURL('image/jpeg', 0.8);
  stopLeaveCamera();
  showMessage('Photo captured!', 'success');
};

const retakeLeavePhoto = () => {
  leaveCapturedPhoto.value = null;
  startLeaveCamera();
};

const submitLeaveClock = async () => {
  if (!leaveCapturedPhoto.value || !locationData.value || processing.value) return;
  processing.value = true;
  try {
    const photoBlob = await fetch(leaveCapturedPhoto.value).then(r => r.blob());
    const formData = new FormData();
    formData.append('photo', photoBlob, 'leave-photo.jpg');
    formData.append('latitude',  locationData.value.latitude);
    formData.append('longitude', locationData.value.longitude);
    formData.append('accuracy',  locationData.value.accuracy || '');

    const endpoint = leaveClockMode.value === 'in'
      ? '/employee/attendance/leave-in'
      : '/employee/attendance/leave-out';

    const response = await api.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    showMessage(response.data.message || 'Recorded successfully!', 'success');
    showLeaveClockModal.value = false;
    leaveCapturedPhoto.value = null;
    await loadTodayRecord();
    await loadShortLeaveSchedule();
  } catch (error) {
    showMessage(error.response?.data?.message || 'Failed to record leave action', 'error');
  } finally {
    processing.value = false;
  }
};

const startLeaveElapsedTimer = () => {
  if (leaveElapsedInterval) return;
  const update = () => {
    if (!activeShortLeaveSession.value?.leaveInTime) return;
    leaveElapsedMinutes.value = Math.round(
      (Date.now() - new Date(activeShortLeaveSession.value.leaveInTime)) / 60000
    );
  };
  update();
  leaveElapsedInterval = setInterval(update, 30000);
};

const stopLeaveElapsedTimer = () => {
  if (leaveElapsedInterval) {
    clearInterval(leaveElapsedInterval);
    leaveElapsedInterval = null;
  }
};

// ─────────────────────────────────────────────────────────────────────────────

const startWorkDurationTimer = () => {
  if (workDurationInterval) return;

  workDurationInterval = setInterval(() => {
    if (
      todayRecord.value?.clock_in_time &&
      !todayRecord.value?.clock_out_time
    ) {
      const clockInTime = new Date(todayRecord.value.clock_in_time);
      const now = new Date();
      const diffMs = now - clockInTime;
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      workDuration.value = `${hours}h ${minutes}m`;
    }
  }, 1000);
};

const formatTime = datetime => {
  return new Date(datetime).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = duration => {
  if (!duration || duration === 0) return '0h 0m';
  
  let totalMinutes;
  
  // Check if duration is in decimal hours (like 0.5333) or in minutes (like 32)
  if (duration < 24 && duration % 1 !== 0) {
    // Likely decimal hours - convert to minutes
    totalMinutes = Math.round(duration * 60);
  } else {
    // Likely already in minutes
    totalMinutes = Math.round(duration);
  }
  
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  
  return `${hours}h ${mins}m`;
};

const showMessage = (text, type = 'success') => {
  message.value = text;
  messageType.value = type;

  if (type === 'success') {
    setTimeout(() => {
      clearMessage();
    }, 5000);
  }
};

const clearMessage = () => {
  message.value = '';
  messageType.value = 'success';
};

// Break management methods
const checkBreakStatus = async () => {
  try {
    const response = await api.get('/employee/breaks/status');
    if (response.data.success) {
      const data = response.data.data;

      breakStatus.value = data.currentBreak;
      upcomingBreak.value = data.upcomingBreak;

      // Set automatic action flags
      shouldAutoClockOut.value = data.automaticAction.shouldClockOut;
      shouldAutoClockIn.value = data.automaticAction.shouldClockIn;
      autoActionMessage.value = data.automaticAction.message || '';

      // Show notifications for break transitions
      if (data.automaticAction.shouldClockOut) {
        showMessage(data.automaticAction.message, 'info');
      } else if (data.automaticAction.shouldClockIn) {
        showMessage(data.automaticAction.message, 'info');
      }
    }
  } catch (error) {
    console.error('Error checking break status:', error);
  }
};

const handleAutoClockOut = async () => {
  if (!shouldAutoClockOut.value) return;

  processing.value = true;

  try {
    // Get current location if available
    const locationData = {
      latitude: locationData.value?.latitude || null,
      longitude: locationData.value?.longitude || null,
      accuracy: locationAccuracy.value || null
    };

    const response = await api.post('/employee/breaks/auto-clock-out', locationData);

    if (response.data.success) {
      showMessage(response.data.message, 'success');

      // Reset auto clock-out flag
      shouldAutoClockOut.value = false;
      autoActionMessage.value = '';

      // Reload attendance and break status
      await Promise.all([
        loadTodayRecord(),
        checkBreakStatus()
      ]);
    }
  } catch (error) {
    console.error('Error handling auto clock-out:', error);
    showMessage(error.response?.data?.message || 'Failed to clock out for break', 'error');
  } finally {
    processing.value = false;
  }
};

const processBreakEnd = async (breakLeaveId) => {
  try {
    const response = await api.post('/employee/breaks/process-end', {
      breakLeaveId: breakLeaveId
    });

    if (response.data.success) {
      showMessage(response.data.message, 'success');

      // Reset break status
      breakStatus.value = null;
      shouldAutoClockIn.value = true;

      // Reload attendance status
      await loadTodayRecord();
    }
  } catch (error) {
    console.error('Error processing break end:', error);
    showMessage(error.response?.data?.message || 'Failed to process break end', 'error');
  }
};

const startNewSession = () => {
  // Reset the component state to allow a new clock-in
  capturedPhoto.value = null;
  message.value = '';
  
  // Force showing the clock-in interface
  forceShowClockIn.value = true;
  
  // Reload today's record to reset the state
  loadTodayRecord();
  
  showMessage('Ready to start a new work session!', 'success');
};

const getWorkDayAction = () => {
  if (!todayRecord.value) {
    return 'Start Your Work Day';
  }
  
  if (forceShowClockIn.value || (todayRecord.value.clock_out_time && !todayRecord.value.isClockedIn)) {
    return 'Start New Work Session';
  }
  
  if (todayRecord.value.isClockedIn) {
    return 'End Current Session';
  }
  
  return 'Start Your Work Day';
};

const getWorkDayInstructions = () => {
  if (!todayRecord.value) {
    return 'Take a photo and allow location access to clock in';
  }
  
  if (forceShowClockIn.value || (todayRecord.value.clock_out_time && !todayRecord.value.isClockedIn)) {
    return 'Take a photo and allow location access to start your new work session';
  }
  
  if (todayRecord.value.isClockedIn) {
    return 'Take a photo to clock out of current session';
  }
  
  return 'Take a photo and allow location access to clock in';
};

// Lifecycle
onMounted(() => {
  updateCurrentTime();
  timeInterval = setInterval(updateCurrentTime, 1000);

  loadUserProfile();
  loadTodayRecord();
  loadShortLeaveSchedule();
  loadFieldLocations();
  checkDeviceApproval();

  // Initialize break checking
  checkBreakStatus();

  // Get location immediately and refresh it periodically
  getCurrentLocation();

  // Refresh location every 2 minutes to ensure we always have location data
  const locationInterval = setInterval(() => {
    getCurrentLocation();
  }, 120000);

  // Start work duration timer if already clocked in
  setTimeout(() => {
    if (
      todayRecord.value?.clock_in_time &&
      !todayRecord.value?.clock_out_time
    ) {
      startWorkDurationTimer();
    }
  }, 1000);

  // Auto-refresh attendance status every 30 seconds to catch approvals
  const refreshInterval = setInterval(() => {
    loadTodayRecord();
  }, 30000);

  // Refresh short leave schedule every 30 seconds so the Leave In button
  // appears right as the approved window opens (5 min before scheduled start)
  shortLeaveScheduleInterval = setInterval(() => {
    loadShortLeaveSchedule();
  }, 30000);

  // Check break status every 30 seconds for automatic transitions
  breakCheckInterval = setInterval(() => {
    checkBreakStatus();
  }, 30000);
  
  // Store the interval so we can clean it up on unmount
  onUnmounted(() => {
    stopCamera();
    stopLeaveCamera();
    stopLeaveElapsedTimer();
    if (workDurationInterval) clearInterval(workDurationInterval);
    if (timeInterval)         clearInterval(timeInterval);
    if (refreshInterval)      clearInterval(refreshInterval);
    if (locationInterval)     clearInterval(locationInterval);
    if (breakCheckInterval)   clearInterval(breakCheckInterval);
    if (shortLeaveScheduleInterval) clearInterval(shortLeaveScheduleInterval);
  });
});
</script>

<style scoped>
.clock-interface {
  max-width: 600px;
  margin: 0 auto;
  padding: 0;
  background: transparent;
}

.clock-container {
  background: transparent;
  border-radius: 16px;
  padding: 0;
}

.status-card {
  text-align: center;
  margin-bottom: 30px;
  padding: 25px;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  border: 1px solid rgba(129, 140, 248, 0.35);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.25);
  border-radius: 16px;
  color: white;
}

.current-time h2 {
  margin: 0 0 10px 0;
  font-size: 2.5rem;
  font-weight: 300;
  letter-spacing: 2px;
}

.current-time p {
  margin: 0;
  opacity: 0.9;
  font-size: 1.1rem;
}

.current-status {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 500;
  font-size: 14px;
}

.status-badge.not-started {
  background: rgba(255, 255, 255, 0.2);
}

.status-badge.active {
  background: rgba(76, 175, 80, 0.9);
}

.status-badge.pending {
  background: rgba(255, 152, 0, 0.9);
}

.status-badge.completed {
  background: rgba(76, 175, 80, 0.9);
}

.work-duration {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  opacity: 0.9;
}

.message {
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.message.success {
  background: rgba(52, 211, 153, 0.09);
  border: 1px solid rgba(52, 211, 153, 0.3);
  color: #6ee7b7;
}

.message.error {
  background: rgba(248, 113, 113, 0.09);
  border: 1px solid rgba(248, 113, 113, 0.3);
  color: #fca5a5;
}

.message-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.message-close {
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.7;
}

.message-close:hover {
  opacity: 1;
}

.device-warning {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: rgba(251, 191, 36, 0.08);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 12px;
  margin-bottom: 25px;
  color: #fcd34d;
}

.device-warning h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
}

.device-warning p {
  margin: 0 0 10px 0;
  font-size: 14px;
}

.btn-link {
  background: none;
  border: none;
  color: #818cf8;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
}

.completed-day {
  text-align: center;
  padding: 40px 20px;
}

.completion-icon {
  color: #34d399;
  margin-bottom: 20px;
}

.completed-day h3 {
  color: #34d399;
  margin-bottom: 10px;
  font-size: 1.8rem;
}

.completed-day p {
  color: #94a3b8;
  margin-bottom: 30px;
  font-size: 1.1rem;
}

.day-summary {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  padding: 25px;
  display: grid;
  gap: 15px;
  max-width: 400px;
  margin: 0 auto;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item label {
  font-weight: 600;
  color: #cbd5e1;
}

.summary-item span {
  color: #94a3b8;
}

.new-session-section {
  margin-top: 30px;
  padding: 25px;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  border: 1px solid rgba(129, 140, 248, 0.35);
  border-radius: 16px;
  text-align: center;
}

.new-session-info {
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  font-weight: 500;
}

.btn-new-session {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: rgba(255, 255, 255, 0.95);
  color: #6366f1;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.btn-new-session:hover:not(:disabled) {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.btn-new-session:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.session-info {
  background: rgba(99, 102, 241, 0.07);
  border: 1px solid rgba(99, 102, 241, 0.22);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
}

.session-detail {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #cbd5e1;
}

.session-detail.pending {
  color: #fbbf24;
  font-weight: 600;
}

.session-detail:last-child {
  margin-bottom: 0;
}

.short-leave-section {
  margin-top: 20px;
  padding: 20px;
  background: rgba(251, 191, 36, 0.07);
  border-radius: 12px;
  border: 1px solid rgba(251, 191, 36, 0.28);
}

.short-leave-section h4 {
  margin: 0 0 8px 0;
  color: #fcd34d;
  font-size: 1.1rem;
  font-weight: 600;
}

.short-leave-section p {
  margin: 0 0 15px 0;
  color: rgba(252, 211, 77, 0.75);
  font-size: 14px;
}

.btn-leave {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-leave:hover {
  background: #d97706;
}

/* Active short-leave banner */
.active-leave-banner {
  margin-top: 20px;
  padding: 20px;
  background: rgba(251, 191, 36, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(245, 158, 11, 0.45);
}

.active-leave-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
}

.active-leave-icon {
  background: #f59e0b;
  color: white;
  border-radius: 50%;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.active-leave-banner h4 {
  margin: 0 0 4px 0;
  color: #fcd34d;
  font-size: 1rem;
  font-weight: 700;
}

.active-leave-banner p {
  margin: 0;
  color: rgba(252, 211, 77, 0.75);
  font-size: 13px;
}

.btn-leave-out {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
  width: 100%;
  justify-content: center;
}

.btn-leave-out:hover { background: #059669; }

.btn-primary.leave-in-btn  { background: #f59e0b; }
.btn-primary.leave-in-btn:hover:not(:disabled) { background: #d97706; }
.btn-primary.leave-out-btn { background: #10b981; }
.btn-primary.leave-out-btn:hover:not(:disabled) { background: #059669; }

.clock-action-section h3 {
  color: #f1f5f9;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.clock-action-section p {
  color: #94a3b8;
  margin-bottom: 25px;
}

.mode-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

.mode-option {
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: rgba(255, 255, 255, 0.03);
}

.mode-option:hover {
  border-color: rgba(99, 102, 241, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.2);
}

.mode-option.active {
  border-color: rgba(99, 102, 241, 0.6);
  background: rgba(99, 102, 241, 0.1);
}

.mode-option svg {
  color: #818cf8;
  margin-bottom: 10px;
}

.mode-option span {
  display: block;
  font-weight: 600;
  margin-bottom: 5px;
  color: #e2e8f0;
}

.mode-option p {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.field-location-section {
  margin-bottom: 25px;
}

.field-location-section h4 {
  margin-bottom: 15px;
  color: #e2e8f0;
  font-size: 1.1rem;
}

.field-locations {
  display: grid;
  gap: 10px;
  margin-bottom: 15px;
}

.field-location-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
  background: rgba(255, 255, 255, 0.03);
}

.field-location-item:hover {
  border-color: rgba(99, 102, 241, 0.5);
}

.field-location-item.selected {
  border-color: rgba(99, 102, 241, 0.6);
  background: rgba(99, 102, 241, 0.1);
}

.field-location-item svg {
  color: #818cf8;
  flex-shrink: 0;
}

.field-location-item strong {
  display: block;
  color: #e2e8f0;
  margin-bottom: 2px;
}

.field-location-item p {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.custom-location-input {
  margin-top: 15px;
}

.field-input {
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.04);
  color: #e2e8f0;
  transition: border-color 0.3s;
}

.field-input:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.6);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.camera-section {
  margin-bottom: 25px;
}

.camera-section h4 {
  margin-bottom: 8px;
  color: #e2e8f0;
  font-size: 1.2rem;
  font-weight: 600;
}

.camera-description {
  color: #94a3b8;
  margin-bottom: 20px;
  font-size: 14px;
}

.camera-placeholder {
  text-align: center;
  padding: 40px 20px;
  border: 2px dashed rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  position: relative;
}

.camera-icon-container {
  position: relative;
  display: inline-block;
  margin-bottom: 20px;
}

.camera-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: rgba(99, 102, 241, 0.25);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.4);
    opacity: 0;
  }
}

.camera-guidelines {
  margin: 20px 0;
  text-align: left;
  max-width: 250px;
  margin-left: auto;
  margin-right: auto;
}

.guideline-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #94a3b8;
}

.guideline-item svg {
  color: #34d399;
  flex-shrink: 0;
}

.btn-camera {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-camera:hover {
  background: #4f46e5;
}

/* Add these CSS fixes to your component styles */

.camera-container {
  text-align: center;
  padding: 20px;
}

.camera-wrapper {
  position: relative;
  display: inline-block;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  margin-bottom: 20px;
}

.camera-preview {
  width: 100%;
  max-width: 400px;
  height: 300px;
  object-fit: cover;
  display: block;
  border-radius: 12px;
  background: #000;
  /* Deliberately not mirrored - the live preview should match exactly what
     gets captured and sent for face verification, so there's no left/right
     confusion between what the employee sees and the actual reference photo. */
}

/* Camera status overlay */
.camera-status-overlay {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

/* Face guide overlay */
.face-guide-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 5;
}

.face-outline {
  width: 120px;
  height: 160px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 60px;
  background: transparent;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3);
}

.face-guide-text {
  margin-top: 15px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  background: rgba(0, 0, 0, 0.5);
  padding: 6px 12px;
  border-radius: 6px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .camera-preview {
    max-width: 350px;
    height: 250px;
  }

  .face-outline {
    width: 100px;
    height: 140px;
    border-radius: 50px;
  }
}

@media (max-width: 480px) {
  .camera-preview {
    max-width: 300px;
    height: 220px;
  }
}

/* Loading state for video */
.camera-preview[data-loading='true'] {
  background: #333;
  opacity: 0.7;
}

.camera-status-info {
  margin-bottom: 15px;
  text-align: center;
}

.video-container {
  position: relative;
  display: inline-block;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  margin-bottom: 20px;
}

.video-stream {
  width: 400px;
  height: 300px;
  object-fit: cover;
  display: block;
  border-radius: 12px;
  background: #000;
}

.face-guide-overlay-simple {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.face-oval {
  width: 120px;
  height: 160px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 60px;
  background: transparent;
}

.guide-text {
  margin-top: 10px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 8px;
  border-radius: 4px;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-indicator.ready {
  background: rgba(76, 175, 80, 0.9);
  color: white;
}

.status-indicator.loading {
  background: rgba(255, 152, 0, 0.9);
  color: white;
}

.camera-controls {
  text-align: center;
  margin-top: 15px;
  display: flex;
  justify-content: center;
  gap: 15px;
}

.btn-capture {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-capture:hover {
  background: #059669;
}

.btn-capture:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: #64748b;
  cursor: not-allowed;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.06);
  color: #cbd5e1;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
}

.photo-preview {
  text-align: center;
  margin-top: 20px;
}

.preview-image {
  width: 100%;
  max-width: 300px;
  height: 200px;
  object-fit: cover;
  border-radius: 12px;
  border: 3px solid rgba(255, 255, 255, 0.12);
  margin-bottom: 15px;
}

.photo-success {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #34d399;
  font-weight: 600;
  margin-bottom: 15px;
}

.photo-controls {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.btn-retake {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-retake:hover {
  background: #d97706;
}

.requirements-checklist {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
}

.requirements-checklist h4 {
  margin: 0 0 15px 0;
  color: #e2e8f0;
  font-size: 1.1rem;
  font-weight: 600;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
  color: #94a3b8;
  transition: color 0.3s;
}

.requirement-item.met {
  color: #34d399;
}

.requirement-item svg {
  color: #475569;
  transition: color 0.3s;
}

.requirement-item.met svg {
  color: #10b981;
}

.clock-buttons {
  text-align: center;
}

.clock-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 180px;
  justify-content: center;
}

.clock-in-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.clock-in-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.clock-out-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.clock-out-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.clock-btn:disabled {
  background: rgba(255, 255, 255, 0.08);
  color: #64748b;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.clock-btn.processing {
  opacity: 0.8;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(2, 4, 12, 0.72);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: rgba(11, 16, 31, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 20px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 25px 0 25px;
  margin-bottom: 20px;
}

.modal-header h3 {
  margin: 0;
  color: #f1f5f9;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-header button {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.3s;
}

.modal-header button:hover {
  background: rgba(255, 255, 255, 0.06);
}

.short-leave-form {
  padding: 0 25px 25px 25px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #94a3b8;
}

.form-group select,
.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.04);
  color: #e2e8f0;
  transition: border-color 0.3s;
}

.form-group select:focus,
.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.6);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.form-group textarea {
  min-height: 80px;
  resize: vertical;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 25px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-primary:hover:not(:disabled) {
  background: #4f46e5;
}

.btn-primary:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: #64748b;
  cursor: not-allowed;
}

/* Responsive adjustments for video */
@media (max-width: 768px) {
  .clock-interface {
    padding: 0;
  }

  .clock-container {
    padding: 0;
  }

  .current-time h2 {
    font-size: 2rem;
  }

  .mode-selection {
    grid-template-columns: 1fr;
  }

  .video-stream {
    width: 100%;
    max-width: 350px;
    height: 250px;
  }

  .clock-btn {
    width: 100%;
    max-width: 300px;
  }

  .modal-content {
    margin: 10px;
    max-width: calc(100% - 20px);
  }

  .camera-controls {
    flex-direction: column;
    gap: 10px;
  }

  .btn-capture,
  .btn-secondary {
    width: 100%;
  }

  .btn-new-session {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 480px) {
  .current-time h2 {
    font-size: 1.8rem;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}

/* Animation classes */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.clock-container {
  animation: fadeIn 0.3s ease-out;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Break Management Styles */
.break-status-section {
  margin: 20px 0;
}

.break-status-card {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border-radius: 12px;
  padding: 20px;
  color: white;
  text-align: center;
}

.break-status-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 15px;
}

.break-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.break-status-header h4 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.break-status-header p {
  margin: 0;
  opacity: 0.9;
  font-size: 0.9rem;
}

.break-time-remaining {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 1.1rem;
  font-weight: 500;
}

.break-end-time {
  opacity: 0.8;
  font-size: 0.9rem;
}

.upcoming-break-warning {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px;
  background: rgba(251, 191, 36, 0.08);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 12px;
  margin: 15px 0;
  color: #fcd34d;
}

.warning-icon {
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 2px;
}

.upcoming-break-warning h4 {
  margin: 0 0 5px 0;
  font-size: 1rem;
  font-weight: 600;
}

.upcoming-break-warning p {
  margin: 0;
  font-size: 0.9rem;
}

.auto-action-notice {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: rgba(56, 189, 248, 0.08);
  border: 1px solid rgba(56, 189, 248, 0.3);
  border-radius: 12px;
  margin: 20px 0;
  color: #7dd3fc;
}

.notice-icon {
  color: #38bdf8;
  flex-shrink: 0;
  margin-top: 2px;
}

.auto-action-notice h4 {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.auto-action-notice p {
  margin: 0 0 15px 0;
  font-size: 0.9rem;
}

.btn-auto-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-auto-action:hover:not(:disabled) {
  background: #2563eb;
}

.btn-auto-action:disabled {
  background: rgba(255, 255, 255, 0.12);
  cursor: not-allowed;
}

/* Message type for info */
.message.info {
  background: rgba(56, 189, 248, 0.09);
  border: 1px solid rgba(56, 189, 248, 0.3);
  color: #7dd3fc;
}

.short-leave-block-hint {
  margin-top: 12px;
  padding: 10px 14px;
  background: rgba(251, 191, 36, 0.08);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 10px;
  font-size: 13px;
  color: #fcd34d;
  text-align: center;
}
</style>
